import { useState } from "react";

import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/system";

import { CloseIcon, MessageAddIcon, MoreIcon, SettingsIcon, SidebarIcon } from "../Icons/Icons";
import { ChatHistory } from "./ChatHistory";

export default function ChatSidebar({
  chats,
  setChats,
  currentChatId,
  setCurrentChatId,
  isSettingsOpen,
  setIsSettingsOpen,
}) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const isMobile = useMediaQuery("(max-width: 809px)");

  const handleNewChat = () => {
    // Instead of creating an empty chat immediately, just set currentChatId to null
    // This indicates we're in "new chat" mode - the chat will be created when user sends first message
    setCurrentChatId(null);
    // Close settings when starting a new chat
    setIsSettingsOpen(false);
  };

  const handleSetCurrentChatId = (chatId) => {
    setCurrentChatId(chatId);
    // Close settings when selecting a chat
    setIsSettingsOpen(false);
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  };

  const handleSettingsToggle = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  return (
    <div className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
      <div
        className="sidebar-header"
        style={{
          alignSelf: isSidebarCollapsed ? "center" : "start",
          alignItems: isMobile ? "center" : "flex-start",
        }}
      >
        {isMobile ? (
          <div className="sidebar-header-mobile">
            <MessageAddIcon
              onClick={() => {
                handleNewChat();
                setIsSidebarCollapsed(true);
              }}
              style={{ fill: "none", marginRight: "0.75rem", cursor: "pointer" }}
            />

            {isSidebarCollapsed ? (
              <MoreIcon className="more-icon" onClick={() => setIsSidebarCollapsed(false)} />
            ) : (
              <CloseIcon className="close-icon" onClick={() => setIsSidebarCollapsed(true)} />
            )}
          </div>
        ) : (
          <SidebarIcon
            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
            sx={{
              cursor: "pointer",
              rotate: isSidebarCollapsed ? "0deg" : "180deg",
            }}
          />
        )}
      </div>

      <div className="new-chat-button">
        {isSidebarCollapsed ? (
          <Tooltip title="New Chat" placement="right">
            <IconButton
              onClick={handleNewChat}
              sx={{
                backgroundColor: "secondary.main",
                "&:hover": { bgcolor: "secondary.main" },
                color: "common.white",
                width: "100%",
                height: "48px",
                borderRadius: "48px",
              }}
            >
              <MessageAddIcon color="white" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            onClick={handleNewChat}
            sx={{
              backgroundColor: "secondary.main",
              "&:hover": { bgcolor: "secondary.main" },
              color: "common.white",
              width: "100%",
              padding: 1,
            }}
          >
            <Box className="new-chat-text-content">
              <MessageAddIcon sx={{ mr: 1, flexShrink: 0 }} color="white" />
              <span>New chat</span>
            </Box>
          </Button>
        )}
      </div>

      <div className="sidebar-content" style={{ flex: 1, overflow: "hidden" }}>
        {!isSidebarCollapsed && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              History
            </Typography>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <ChatHistory
                chats={chats}
                setChats={setChats}
                currentChatId={currentChatId}
                setCurrentChatId={handleSetCurrentChatId}
              />
            </Box>
          </Box>
        )}
      </div>
      <div className="sidebar-footer">
        <div className="new-chat-button">
          {isSidebarCollapsed ? (
            <Tooltip title="Settings" placement="right">
              <IconButton
                onClick={handleSettingsToggle}
                sx={{
                  backgroundColor: isSettingsOpen ? "primary.main" : "secondary.main",
                  "&:hover": { bgcolor: isSettingsOpen ? "primary.main" : "secondary.main" },
                  color: "common.white",
                  width: "100%",
                  height: "48px",
                  borderRadius: "48px",
                }}
              >
                <SettingsIcon color="white" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              onClick={handleSettingsToggle}
              sx={{
                backgroundColor: isSettingsOpen ? "primary.main" : "secondary.main",
                "&:hover": { bgcolor: isSettingsOpen ? "primary.main" : "secondary.main" },
                color: "common.white",
                width: "100%",
                padding: 1,
              }}
            >
              <Box className="new-chat-text-content">
                <SettingsIcon sx={{ mr: 1, flexShrink: 0 }} color="white" />
                <span>Settings</span>
              </Box>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
